import React, { useState, useMemo, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { auth, db, functions } from "../../config/firebaseConfig";
import { LockClosedIcon, UserIcon } from "@heroicons/react/20/solid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "../../components/catalyst/badge";
import { getStatusBadgeClass } from "../../util/statusBadge";
import { httpsCallable } from "firebase/functions";
import { useToast } from "../../util/ToastProvider";
import { getAuth } from "firebase/auth";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

type TableReport = {
  member: string;
  status: string;
  lastUpdated: string;
  template: {
    templateId: string;
    templateTitle: string;
  };
  id: string;
  review?: {
    reviewerName: string;
    reviewerId: string;
  };
};

const statusOptions = ["All", "Pending", "Blocked", "Failed", "Draft", "In-Review", "Ready", "Sent"] as const;
const statusOrder = ["Pending","Blocked", "Failed", "Draft", "In-Review", "Ready", "Sent"];

export default function AdminHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(
    searchParams.get("q") ?? ""
  );
const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
  searchParams.get("f")
    ? [searchParams.get("f") as string]
    : ["All"]
);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [reportsData, setReportsData] = useState<TableReport[]>([]);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const { user } = useSelector((s: RootState) => s.auth);


  // ✅ Only one selected report ID
  const [selectedReport, setSelectedReport] = useState<string | null>(null);

  const [showDialog, setShowDialog] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [sending, setSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportLoading, setReportLoading] = useState(false);
  const PAGE_SIZE = 10;

  const [currentPage, setCurrentPage] = useState(1);

  const nav = useNavigate();
  const toaster = useToast();
  const auth = getAuth();

  useEffect(() => {
    setReportLoading(true)
    callReport('123').then(t => {
      getAllReportsWithUsers().then((v: any[]) => {
        setReportLoading(false)
        setReportsData(transformReports(v));
      });
    })
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
  
    // search text
    if (search.trim()) {
      params.set("q", search.trim());
    }
  
    // status filter (ignore All)
    if (!selectedStatuses.includes("All") && selectedStatuses.length === 1) {
      params.set("f", selectedStatuses[0]);
    }
  
    setSearchParams(params, { replace: true });
  }, [search, selectedStatuses]);
  

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedStatuses]);

  async function getAllReportsWithUsers(): Promise<any[]> {
    try {
      
      const reportsRef = collection(db, "reports");
      const snapshot = await getDocs(reportsRef);
      const reports = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log(`Loaded ${reports.length} reports`, reports);
      return reports;
    } catch (error: any) {
      console.error("Error fetching reports:", error.message);
      throw error;
  }
}

  function transformReports(rawReports: any[]): TableReport[] {
    return rawReports.map((r) => ({
      id: r.id,
      member: r.user?.name || "Unknown",
      status: capitalizeStatus(r.status),
      template: r.template || { templateId: "", templateTitle: "Untitled" },
      lastUpdated: new Date(r.updatedAt?.seconds * 1000 || Date.now()).toISOString(),
      review: r.review ?? null,
    }));
  }

  function capitalizeStatus(status: string): string {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }
  

  const toggleStatus = (status: string) => {
    if (status === "All") {
      setSelectedStatuses(["All"]);
    } else {
      setSelectedStatuses((prev) => {
        const withoutAll = prev.filter((s) => s !== "All");
        return withoutAll.includes(status)
          ? withoutAll.filter((s) => s !== status)
          : [...withoutAll, status];
      });
    }
  };

  const filteredReports = useMemo(() => {
    const filtered = reportsData.filter((r) => {
      const matchesSearch =
        r.template.templateTitle.toLowerCase().includes(search.toLowerCase()) ||
        r.member.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        selectedStatuses.includes("All") || selectedStatuses.includes(r.status);

      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const dateA = new Date(a.lastUpdated).getTime();
      const dateB = new Date(b.lastUpdated).getTime();
      if (dateA !== dateB) {
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      }
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    });
  }, [search, selectedStatuses, sortDirection, reportsData]);


  const totalPages = Math.ceil(filteredReports.length / PAGE_SIZE);

  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredReports.slice(start, start + PAGE_SIZE);
  }, [filteredReports, currentPage]);

  // -------------------------
  // 🔵 SINGLE selection logic
  // -------------------------
  const handleSelectRow = (id: string) => {
    setSelectedReport((prev) => (prev === id ? null : id));
  };

  const selectedReportData = filteredReports.find((r) => r.id === selectedReport);

  const isSendEnabled =
    selectedReportData && selectedReportData.status.toLowerCase() === "ready";

  // -------------------------
  // 🔵 Generate Email
  // -------------------------
  const handleSendReports = async () => {
    if (!selectedReport) return;

    try {
      setIsLoading(true);
      const performFn = httpsCallable(functions, "performReportReview");

      const res: any = await performFn({
        action: "generateEmail",
        reportId: selectedReport, // ✅ single report
      });

      setEmailContent(res.data?.generatedEmail || "");
      setShowDialog(true);
    } catch (err: any) {
      console.error("Email generation error:", err);
      setEmailContent("Error generating email content.");
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------------
  // 🔵 Final Send
  // -------------------------
  const handleSendEmail = async () => {
    if (!selectedReport) return;
  
    try {
      setSending(true);
      const sendFn = httpsCallable(functions, "performReportReview");
  
      const res: any = await sendFn({
        action: "SendEmailToUser",
        reportId: selectedReport,
        emailString: emailContent,
      });
  
      toaster.success(res.data?.message || "Email sent");
  
      // 💥 Refresh data after email is sent
      getAllReportsWithUsers().then((v) => {
        setReportsData(transformReports(v));
      });
  
      setShowDialog(false);
      setSelectedReport(null);
    } catch (err: any) {
      toaster.error(err.message || "Failed to send emails.");
    } finally {
      setSending(false);
    }
  };

  const callReport = async (id:string) => {
    const performReportReviewTask = httpsCallable(functions, "performReportReview");
    const res: any = await performReportReviewTask({
      action: "refreshLock",
      reportId: id,
    });
  }
  

  return (
    <div className=" mx-8 my-6">
      <div className="flex justify-between  mb-4">
        <h1 className="text-xl font-semibold">Reports</h1>

        {(user?.role === "admin" || user?.role === "approver") && (
        <button
          onClick={handleSendReports}
          disabled={!isSendEnabled || isLoading}
          className={`px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition flex items-center justify-center gap-2 ${
            isSendEnabled && !isLoading
              ? "bg-brand-600 text-white hover:bg-brand-700"
              : "bg-gray-300 text-gray-600 cursor-not-allowed"
          }`}
        >
          {isLoading ? "Generating..." : "Send report"}
        </button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col items-start gap-6 mb-6">
        <input
          placeholder="Search by report or member name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-[60%] px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400"
        />

        <div className="flex flex-wrap gap-3">
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => toggleStatus(status)}
              className={`px-3 py-1 rounded-full text-sm border ${
                selectedStatuses.includes(status)
                  ? "bg-brand-600 text-white border-blue-600"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              {status}
            </button>
          ))}
          <button
            onClick={() => setShowStatusDialog(true)}
            className="flex items-center gap-1 ml-3 bg-brand-200 px-2 py-1 text-[14px] font-medium rounded-lg text-brand-600"
          >
            <UserIcon className="h-4 w-4 text-brand-600" />
            What do statuses mean?
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="px-4 py-2 w-[40px]"></th>
              <th className="px-4 py-2">Report</th>
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2">Status</th>
              <th
                className="px-4 py-2 cursor-pointer select-none"
                onClick={() =>
                  setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                }
              >
                Last updated on{" "}
                <span className="text-sm">{sortDirection === "asc" ? "↑" : "↓"}</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedReports.length > 0 ? (
              paginatedReports.map((r) => (
                <tr
                  key={r.id}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === "INPUT") return;
                  
                    const allowedStatuses = ["Draft", "Ready", "Sent", "In_review", "Failed", "Outdated"];
                    if (!allowedStatuses.includes(r.status)) return;
                  
                    const params = new URLSearchParams();
                  
                    // ✅ IMPORTANT: tell ViewReport where we came from
                    params.set("from", "admin-list");
                  
                    if (search.trim()) params.set("q", search.trim());
                    if (!selectedStatuses.includes("All") && selectedStatuses.length === 1) {
                      params.set("f", selectedStatuses[0]);
                    }
                  
                    nav(`/admin/report/${r.id}?${params.toString()}`);
                  }}
                  
                  className={`
                    transition-colors
                    ${["Draft", "Ready", "Sent", "In_review", "Failed", 'Outdated'].includes(r.status)
                      ? "cursor-pointer hover:bg-gray-50"
                      : "cursor-not-allowed opacity-70"}
                  `}
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedReport === r.id}
                      onChange={() => handleSelectRow(r.id)}
                    />
                  </td>
                  <td className="px-4 py-2 truncate max-w-[30ch]">{r.template.templateTitle}</td>
                  <td className="px-4 py-2 truncate max-w-[20ch]">{r.member}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`${getStatusBadgeClass(
                          r.status
                        )} capitalize bold text-xs px-2 py-0.5`}
                      >
                        {r.status}
                      </Badge>

                      {r.status.toLowerCase() === "in_review" && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                          <LockClosedIcon className="h-4 w-4 text-gray-500" />
                          {r.review?.reviewerName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {new Date(r.lastUpdated).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                {isReportLoading && (<td colSpan={5} className="text-center py-6 text-gray-500">
                  Loading Reports
                </td>)}
                {!isReportLoading && (<td colSpan={5} className="text-center py-6 text-gray-500">
                  No Data Available
                </td>)}
              </tr>
            )}
          </tbody>
        </table>
        {filteredReports.length > 0 && (
  <div className="flex items-center justify-between mt-4">
    <span className="text-sm text-gray-600">
      Page {currentPage} of {totalPages}
    </span>

    <div className="flex gap-2">
      <button
        disabled={currentPage === 1}
        onClick={() => setCurrentPage((p) => p - 1)}
        className={`px-3 py-1 rounded-md text-sm ${
          currentPage === 1
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-gray-100 hover:bg-gray-200"
        }`}
      >
        Prev
      </button>

      <button
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage((p) => p + 1)}
        className={`px-3 py-1 rounded-md text-sm ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-gray-100 hover:bg-gray-200"
        }`}
      >
        Next
      </button>
    </div>
  </div>
)}

      </div>

      {/* Email Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-[520px] max-h-[85vh] overflow-auto">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Email notification
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Please edit the email notification. The link to the report will be automatically appended.
            </p>

            <textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              className="w-full h-60 border border-gray-300 rounded-md p-3 text-sm font-normal text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700"
              >
                {sending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}


{showStatusDialog && (
  <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 px-4">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 relative">

      {/* Title */}
      <h2 className="text-lg font-semibold text-gray-900">
        What do statuses mean?
      </h2>

      {/* Subtitle */}
      <p className="text-sm text-gray-600 mt-1">
        Reports can have the following statuses:
      </p>

      {/* Status Image Container */}
      <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-gray-50 flex justify-center">
        <img
          src="/statusInfo.svg"
          alt="Status diagram"
          className="max-w-full h-auto"
        />
      </div>

      {/* Bottom Controls */}
      <div className="flex justify-end mt-6">
        <button
          onClick={() => setShowStatusDialog(false)}
          className="px-5 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
        >
          Close
        </button>
      </div>

    </div>
  </div>
)}





    </div>
  );
}
